import "./style.css";
import { defaultRules, uploadCategories } from "./config";
import { buildReport, type FileBuckets } from "./compute";
import { exportPivotReport, exportReport } from "./exporter";
import type { AppRules, CategoryId, FeePreviewRanges, FileIssue, ReportResult } from "./types";
import { downloadBlob, fileKey, round } from "./utils";

type TabId = "weekly" | "monthly" | "source" | "issues" | "fees";

interface AppState {
  files: FileBuckets;
  feePreviewRanges: FeePreviewRanges;
  rules: AppRules;
  result: ReportResult | null;
  activeTab: TabId;
  busy: boolean;
  status: string;
}

const storageKey = "amazon-weekly-report-rules-v1";
const app = document.querySelector<HTMLDivElement>("#app");

const state: AppState = {
  files: emptyFileBuckets(),
  feePreviewRanges: {},
  rules: loadRules(),
  result: null,
  activeTab: "weekly",
  busy: false,
  status: ""
};

if (!app) throw new Error("App container not found");
const root = app;
render();

function emptyFileBuckets(): FileBuckets {
  return uploadCategories.reduce((acc, category) => {
    acc[category.id] = [];
    return acc;
  }, {} as FileBuckets);
}

function loadRules(): AppRules {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return { ...defaultRules };
  try {
    return { ...defaultRules, ...JSON.parse(raw) };
  } catch {
    return { ...defaultRules };
  }
}

function saveRules(rules: AppRules): void {
  localStorage.setItem(storageKey, JSON.stringify(rules, null, 2));
}

function render(): void {
  root.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar">
        <div class="brand">
          <h1>Amazon 周数据生成器</h1>
          <p>分类上传，浏览器本地计算，导出周数据 Excel。</p>
        </div>
        ${renderUploads()}
        ${renderRules()}
      </aside>
      <main class="main">
        ${renderToolbar()}
        ${renderKpis()}
        ${renderTabs()}
        ${renderCurrentTab()}
      </main>
    </div>
  `;
  bindEvents();
}

function renderUploads(): string {
  return `
    <section class="panel">
      <div class="panel-header">
        <h2>上传数据</h2>
        <div class="actions">
          <button data-action="pick-root-folder">导入根目录</button>
          <button data-action="clear-files">清空</button>
          <input type="file" data-role="root-folder-input" webkitdirectory directory multiple hidden />
        </div>
      </div>
      <div class="panel-body upload-list">
        ${uploadCategories
          .map((category) => {
            const files = state.files[category.id] ?? [];
            const required = category.required ? "必需" : "可选";
            const canPickFolder = category.multiple;
            return `
              <div class="upload-row">
                <div class="upload-title">
                  <span>${category.label}</span>
                  <span class="muted">${required}</span>
                </div>
                <div class="upload-actions">
                  <input class="file-input" type="file" data-file-category="${category.id}" accept="${category.accept}" ${category.multiple ? "multiple" : ""} />
                  ${
                    canPickFolder
                      ? `<button data-action="pick-category-folder" data-category="${category.id}">选择文件夹</button>
                         <input type="file" data-folder-category="${category.id}" webkitdirectory directory multiple hidden />`
                      : ""
                  }
                </div>
                <div class="file-list">${files.length ? summarizeFiles(files) : category.hint}</div>
                ${renderFeePreviewRanges(category.id, files)}
              </div>
            `;
          })
          .join("")}
      </div>
    </section>
  `;
}

function renderFeePreviewRanges(categoryId: CategoryId, files: File[]): string {
  if (categoryId !== "feePreview" || !files.length) return "";
  return `
    <div class="fee-preview-ranges">
      <div class="range-hint">为每个 Fee Preview 限定适用订单日期；留空表示不限制这一端。</div>
      ${files
        .map((file) => {
          const key = fileKey(file);
          const range = state.feePreviewRanges[key] ?? { startDate: "", endDate: "" };
          const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
          return `
            <div class="fee-preview-range-row">
              <div class="range-file" title="${escapeHtml(path)}">${escapeHtml(path)}</div>
              <label>
                <span>开始</span>
                <input type="date" data-fee-preview-key="${escapeHtml(key)}" data-fee-preview-range="startDate" value="${escapeHtml(range.startDate)}" />
              </label>
              <label>
                <span>结束</span>
                <input type="date" data-fee-preview-key="${escapeHtml(key)}" data-fee-preview-range="endDate" value="${escapeHtml(range.endDate)}" />
              </label>
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function renderRules(): string {
  return `
    <section class="panel">
      <div class="panel-header">
        <h2>规则配置</h2>
        <button data-action="reset-rules">重置</button>
      </div>
      <div class="panel-body rules-grid">
        <div class="field">
          <label>佣金率 %</label>
          <input type="number" step="0.01" min="0" data-rule="commissionRate" value="${round(state.rules.commissionRate * 100, 4)}" />
        </div>
        <div class="field">
          <label>旺季月份</label>
          <input type="text" data-rule="peakMonths" value="${state.rules.peakMonths.join(",")}" />
        </div>
        <div class="field">
          <label>旺季倍数</label>
          <input type="number" step="0.1" min="1" data-rule="peakMultiplier" value="${state.rules.peakMultiplier}" />
        </div>
        <div class="field">
          <label>SB SKU 分隔符</label>
          <input type="text" data-rule="sbSkuDelimiter" value="${escapeHtml(state.rules.sbSkuDelimiter)}" />
        </div>
        <div class="field">
          <label>订单时区</label>
          <input type="text" data-rule="timezone" value="${escapeHtml(state.rules.timezone)}" />
        </div>
        <div class="actions">
          <button data-action="export-rules">导出规则</button>
          <button data-action="import-rules">导入规则</button>
          <input type="file" accept=".json" data-role="rules-import" hidden />
        </div>
      </div>
    </section>
  `;
}

function renderToolbar(): string {
  return `
    <div class="status-line">
      <div>
        <strong>生成结果</strong>
        <span class="muted">${state.status ? escapeHtml(state.status) : "等待上传数据"}</span>
      </div>
      <div class="actions">
        <button class="primary" data-action="generate" ${state.busy ? "disabled" : ""}>${state.busy ? "处理中" : "生成周数据"}</button>
        <button data-action="download-pivot" ${state.result && !state.busy ? "" : "disabled"}>导出带透视表</button>
        <button data-action="download" ${state.result && !state.busy ? "" : "disabled"}>导出诊断版</button>
      </div>
    </div>
  `;
}

function renderKpis(): string {
  if (!state.result) {
    return `<div class="empty-state">上传对应表和 ALL ORDER 后生成周数据。</div>`;
  }
  const result = state.result;
  const errors = result.issues.filter((item) => item.severity === "error").length;
  const warnings = result.issues.filter((item) => item.severity === "warning").length;
  const total = result.weeklyRows[result.weeklyRows.length - 1];
  return `
    <div class="kpi-grid">
      ${kpi("日期范围", `${result.stats.earliestDate || "-"} ~ ${result.stats.latestDate || "-"}`)}
      ${kpi("产品数", String(result.stats.productCount))}
      ${kpi("订单行", String(result.stats.orderCount))}
      ${kpi("销售额", money(total?.["销售额"] ?? 0))}
      ${kpi("异常", `${errors} 错误 / ${warnings} 警告`)}
    </div>
  `;
}

function kpi(label: string, value: string): string {
  return `
    <div class="kpi">
      <div class="kpi-label">${escapeHtml(label)}</div>
      <div class="kpi-value">${escapeHtml(value)}</div>
    </div>
  `;
}

function renderTabs(): string {
  if (!state.result) return "";
  const tabs: Array<{ id: TabId; label: string }> = [
    { id: "weekly", label: "周数据透视" },
    { id: "monthly", label: "月数据透视" },
    { id: "source", label: "周数据源" },
    { id: "issues", label: "异常清单" },
    { id: "fees", label: "费用来源" }
  ];
  return `
    <div class="tabs">
      ${tabs
        .map(
          (tab) =>
            `<button class="tab ${state.activeTab === tab.id ? "active" : ""}" data-tab="${tab.id}">${tab.label}</button>`
        )
        .join("")}
    </div>
  `;
}

function renderCurrentTab(): string {
  const result = state.result;
  if (!result) return "";
  if (result.issues.some((item) => item.severity === "error")) {
    return `
      <div class="notice error">存在文件表头或必需文件错误。可先查看异常清单，修正后重新生成。</div>
      ${state.activeTab === "issues" ? renderTable(result.issues, 500) : renderTable(result.issues, 200)}
    `;
  }
  if (state.activeTab === "weekly") return renderTable(result.weeklyRows, 300);
  if (state.activeTab === "monthly") return renderTable(result.monthlyRows, 300);
  if (state.activeTab === "source") return renderTable(result.sourceRows, 500);
  if (state.activeTab === "fees") return renderTable(result.feeChecks, 500);
  return renderTable(result.issues, 500);
}

function renderTable<T extends object>(rows: T[], limit: number): string {
  if (!rows.length) return `<div class="empty-state">无数据</div>`;
  const visible = rows.slice(0, limit);
  const columns = Object.keys(visible[0] as Record<string, unknown>);
  return `
    ${rows.length > limit ? `<div class="notice">当前仅预览前 ${limit} 行，导出的 Excel 会包含全部 ${rows.length} 行。</div>` : ""}
    <div class="table-wrap">
      <table>
        <thead>
          <tr>${columns.map((column) => `<th class="${isTextColumn(column) ? "text" : ""}">${escapeHtml(column)}</th>`).join("")}</tr>
        </thead>
        <tbody>
          ${visible
            .map(
              (row) => `
                <tr>
                  ${columns
                    .map((column) => {
                      const value = (row as Record<string, unknown>)[column];
                      return `<td class="${isTextColumn(column) ? "text" : ""}">${formatCell(value, column)}</td>`;
                    })
                    .join("")}
                </tr>
              `
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function bindEvents(): void {
  document.querySelectorAll<HTMLInputElement>("[data-file-category]").forEach((input) => {
    input.addEventListener("change", () => {
      const id = input.dataset.fileCategory as CategoryId;
      state.files[id] = Array.from(input.files ?? []);
      if (id === "feePreview") syncFeePreviewRanges();
      state.result = null;
      render();
    });
  });

  document.querySelectorAll<HTMLInputElement>("[data-folder-category]").forEach((input) => {
    input.addEventListener("change", () => {
      const id = input.dataset.folderCategory as CategoryId;
      state.files[id] = Array.from(input.files ?? []).filter(isLikelyDataFile);
      if (id === "feePreview") syncFeePreviewRanges();
      state.result = null;
      state.status = `${categoryLabel(id)} 已导入 ${state.files[id].length} 个文件`;
      render();
    });
  });

  document.querySelectorAll<HTMLInputElement>("[data-rule]").forEach((input) => {
    input.addEventListener("change", () => {
      updateRule(input.dataset.rule ?? "", input.value);
      saveRules(state.rules);
      render();
    });
  });

  document.querySelectorAll<HTMLInputElement>("[data-fee-preview-range]").forEach((input) => {
    input.addEventListener("change", () => {
      const key = input.dataset.feePreviewKey;
      const bound = input.dataset.feePreviewRange as "startDate" | "endDate" | undefined;
      if (!key || !bound) return;
      state.feePreviewRanges[key] = {
        ...(state.feePreviewRanges[key] ?? { startDate: "", endDate: "" }),
        [bound]: input.value
      };
      state.result = null;
      state.status = "Fee Preview 适用日期已更新，请重新生成周数据";
      render();
    });
  });

  document.querySelectorAll<HTMLButtonElement>("[data-action]").forEach((button) => {
    button.addEventListener("click", () => handleAction(button.dataset.action ?? ""));
  });

  document.querySelector<HTMLInputElement>("[data-role='root-folder-input']")?.addEventListener("change", (event) => {
    const input = event.currentTarget as HTMLInputElement;
    importRootFolder(Array.from(input.files ?? []));
    render();
  });

  document.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeTab = button.dataset.tab as TabId;
      render();
    });
  });

  document.querySelector<HTMLInputElement>("[data-role='rules-import']")?.addEventListener("change", async (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      state.rules = { ...defaultRules, ...JSON.parse(await file.text()) };
      saveRules(state.rules);
      state.status = "规则已导入";
    } catch (error) {
      state.status = `规则导入失败：${String(error)}`;
    }
    render();
  });
}

function updateRule(rule: string, value: string): void {
  if (rule === "commissionRate") {
    state.rules.commissionRate = Number(value) / 100 || 0;
  } else if (rule === "peakMonths") {
    state.rules.peakMonths = value
      .split(/[,\s，]+/)
      .map((item) => Number.parseInt(item, 10))
      .filter((item) => item >= 1 && item <= 12);
  } else if (rule === "peakMultiplier") {
    state.rules.peakMultiplier = Number(value) || 1;
  } else if (rule === "sbSkuDelimiter") {
    state.rules.sbSkuDelimiter = value || defaultRules.sbSkuDelimiter;
  } else if (rule === "timezone") {
    state.rules.timezone = value || defaultRules.timezone;
  }
}

async function handleAction(action: string): Promise<void> {
  if (action === "clear-files") {
    state.files = emptyFileBuckets();
    state.feePreviewRanges = {};
    state.result = null;
    state.status = "已清空上传文件";
    render();
    return;
  }
  if (action === "pick-root-folder") {
    document.querySelector<HTMLInputElement>("[data-role='root-folder-input']")?.click();
    return;
  }
  if (action === "pick-category-folder") {
    const active = document.activeElement as HTMLElement | null;
    const category = active?.dataset.category;
    if (category) {
      document.querySelector<HTMLInputElement>(`[data-folder-category="${category}"]`)?.click();
    }
    return;
  }
  if (action === "reset-rules") {
    state.rules = { ...defaultRules };
    saveRules(state.rules);
    state.status = "规则已重置";
    render();
    return;
  }
  if (action === "export-rules") {
    const blob = new Blob([JSON.stringify(state.rules, null, 2)], { type: "application/json" });
    downloadBlob(blob, "周数据规则配置.json");
    return;
  }
  if (action === "import-rules") {
    document.querySelector<HTMLInputElement>("[data-role='rules-import']")?.click();
    return;
  }
  if (action === "download" && state.result) {
    exportReport(state.result, state.rules);
    return;
  }
  if (action === "download-pivot" && state.result) {
    state.busy = true;
    state.status = "正在生成带透视表 Excel";
    render();
    try {
      await exportPivotReport(state.result);
      state.status = "带透视表 Excel 已导出";
    } catch (error) {
      state.status = `带透视表导出失败：${String(error)}`;
    } finally {
      state.busy = false;
      render();
    }
    return;
  }
  if (action === "generate") {
    await generate();
  }
}

function summarizeFiles(files: File[]): string {
  const visible = files.slice(0, 6).map((file) => {
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    return escapeHtml(relativePath || file.name);
  });
  const more = files.length > visible.length ? `<br>... 另有 ${files.length - visible.length} 个文件` : "";
  return `${files.length} 个文件<br>${visible.join("<br>")}${more}`;
}

function isLikelyDataFile(file: File): boolean {
  return /\.(txt|csv|xlsx|xls)$/i.test(file.name);
}

function importRootFolder(files: File[]): void {
  const buckets = emptyFileBuckets();
  let imported = 0;
  for (const file of files.filter(isLikelyDataFile)) {
    const category = classifyByPath((file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name);
    if (!category) continue;
    buckets[category].push(file);
    imported += 1;
  }
  for (const category of uploadCategories) {
    if (buckets[category.id].length) {
      state.files[category.id] = category.multiple ? buckets[category.id] : buckets[category.id].slice(0, 1);
    }
  }
  syncFeePreviewRanges();
  state.result = null;
  state.status = `根目录已自动导入 ${imported} 个文件`;
}

function syncFeePreviewRanges(): void {
  const activeKeys = new Set(state.files.feePreview.map((file) => fileKey(file)));
  state.feePreviewRanges = Object.fromEntries(
    Object.entries(state.feePreviewRanges).filter(([key]) => activeKeys.has(key))
  );
  for (const file of state.files.feePreview) {
    const key = fileKey(file);
    if (!state.feePreviewRanges[key]) state.feePreviewRanges[key] = { startDate: "", endDate: "" };
  }
}

function classifyByPath(path: string): CategoryId | null {
  const normalized = path.replace(/\\/g, "/").toLowerCase();
  if (normalized.includes("all order")) return "allOrder";
  if (normalized.includes("fee preview")) return "feePreview";
  if (normalized.includes("storage fee")) return "storageFee";
  if (normalized.includes("transaction")) return "transaction";
  if (normalized.includes("costomer return") || normalized.includes("customer return")) return "customerReturn";
  if (normalized.includes("advetising report/sd") || normalized.includes("advertising report/sd") || /\/sd\//.test(normalized)) {
    return "sdAds";
  }
  if (normalized.includes("advetising report/sp") || normalized.includes("advertising report/sp") || /\/sp\//.test(normalized)) {
    return "spAds";
  }
  if (normalized.includes("advetising report/sb") || normalized.includes("advertising report/sb") || /\/sb\//.test(normalized)) {
    return "sbAds";
  }
  if (normalized.includes("对应表") || normalized.includes("mapping") || normalized.includes("product map")) {
    return "productMap";
  }
  return null;
}

function categoryLabel(id: CategoryId): string {
  return uploadCategories.find((category) => category.id === id)?.label ?? id;
}

async function generate(): Promise<void> {
  state.busy = true;
  state.status = "正在解析文件";
  render();
  try {
    const result = await buildReport(state.files, state.rules, state.feePreviewRanges);
    state.result = result;
    state.activeTab = result.issues.some((item) => item.severity === "error") ? "issues" : "weekly";
    const errorCount = result.issues.filter((item) => item.severity === "error").length;
    const warningCount = result.issues.filter((item) => item.severity === "warning").length;
    state.status = `已生成 ${result.stats.sourceRowCount} 行，${errorCount} 个错误，${warningCount} 个警告`;
  } catch (error) {
    state.result = null;
    state.status = `生成失败：${String(error)}`;
  } finally {
    state.busy = false;
    render();
  }
}

function isTextColumn(column: string): boolean {
  return /名称|name|asin|sku|日期|区间|来源|标签|message|file|category|severity|key|标记|月份|source/i.test(column);
}

function formatCell(value: unknown, column: string): string {
  if (typeof value === "number") {
    if (/率|占比|ACOAS/i.test(column)) return percent(value);
    if (/销售额|利润|配送费|花费|仓储费|退款|成本|佣金|fee|sales|profit|amount/i.test(column)) return money(value);
    return String(round(value, 4));
  }
  return escapeHtml(String(value ?? ""));
}

function money(value: number): string {
  return round(value, 2).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function percent(value: number): string {
  return `${round(value * 100, 2).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}%`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
